// src/components/ProveedoresFicha.js
import React from "react";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { Box, Typography, Paper, IconButton } from "@mui/material";
import { Close, Email, Phone, LocationOn, Edit } from "@mui/icons-material";
import BiotechOutlinedIcon from "@mui/icons-material/BiotechOutlined";

const HEADER_BG = "#90b2b7";

const ProveedoresFicha = ({ proveedor, onClose, onEdit }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  if (!proveedor) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        minHeight: 0,
        maxWidth: 440,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          backgroundColor: HEADER_BG,
          px: 1.5,
          py: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderRadius: 1.5,
          zIndex: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            color: "#fff",
            lineHeight: 1.3,
            pr: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={proveedor.N_PROVEEDOR}
        >
          {proveedor.N_PROVEEDOR.length > 22
            ? `${proveedor.N_PROVEEDOR.slice(0, 22)}…`
            : proveedor.N_PROVEEDOR}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
          <IconButton
            size="small"
            onClick={onEdit}
            aria-label={t("ProveedoresPage.editSupplier")}
            sx={{
              color: "white",
              border: "1px solid rgba(255,255,255,0.6)",
              "&:hover": {
                backgroundColor: "#ffac7e",
                color: "#fff",
              },
            }}
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label={t("ProveedoresPage.closePanel")}
            sx={{
              color: "white",
              border: "1px solid rgba(255,255,255,0.6)",
              "&:hover": {
                backgroundColor: "#ffac7e",
                color: "#fff",
              },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Paper
        sx={{
          mt: 1,
          p: 2,
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <BiotechOutlinedIcon
            sx={{ color: HEADER_BG, mr: 1, flexShrink: 0 }}
          />
          <Typography variant="body2">
            <Box component="span" sx={{ fontWeight: 600, color: theme.palette.text.cuarto }}>
              {t("ProveedoresPage.fieldType")}
            </Box>{" "}
            {proveedor.tipoProveedor?.ProveedorTipo ||
              t("ProveedoresPage.notAvailable")}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
          <LocationOn sx={{ color: "#FFAC7E", mr: 1, flexShrink: 0 }} />
          <Box>
            <Typography variant="body2" sx={{ pb: 0.75 }}>
              <Box component="span" sx={{ fontWeight: 600, color: theme.palette.text.cuarto }}>
                {t("ProveedoresPage.fieldAddress")}
              </Box>{" "}
              {proveedor.DIRECCION || t("ProveedoresPage.notAvailable")}
            </Typography>
            <Typography variant="body2" sx={{ pb: 0.75 }}>
              <Box component="span" sx={{ fontWeight: 600, color: theme.palette.text.cuarto }}>
                {t("ProveedoresPage.fieldNumber")}
              </Box>{" "}
              {proveedor.NVIA || t("ProveedoresPage.notAvailable")}
            </Typography>
            <Typography variant="body2" sx={{ pb: 0.75 }}>
              <Box component="span" sx={{ fontWeight: 600, color: theme.palette.text.cuarto }}>
                {t("ProveedoresPage.fieldPostal")}
              </Box>{" "}
              {proveedor.CP || t("ProveedoresPage.notAvailable")}
            </Typography>
            <Typography variant="body2" sx={{ pb: 0.75 }}>
              <Box component="span" sx={{ fontWeight: 600, color: theme.palette.text.cuarto }}>
                {t("ProveedoresPage.fieldCity")}
              </Box>{" "}
              {proveedor.POBLACION || t("ProveedoresPage.notAvailable")}
            </Typography>
            <Typography variant="body2" sx={{ pb: 0.75 }}>
              <Box component="span" sx={{ fontWeight: 600, color: theme.palette.text.cuarto }}>
                {t("ProveedoresPage.fieldProvince")}
              </Box>{" "}
              {proveedor.provincia || t("ProveedoresPage.notAvailable")}
            </Typography>
            <Typography variant="body2" sx={{ pb: 0.75 }}>
              <Box component="span" sx={{ fontWeight: 600, color: theme.palette.text.cuarto }}>
                {t("ProveedoresPage.fieldCode")}
              </Box>{" "}
              {proveedor.CODPAIS || t("ProveedoresPage.notAvailable")}
            </Typography>
            <Typography variant="body2">
              <Box component="span" sx={{ fontWeight: 600, color: theme.palette.text.cuarto }}>
                {t("ProveedoresPage.fieldCountry")}
              </Box>{" "}
              {proveedor.PAIS || t("ProveedoresPage.notAvailable")}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Email sx={{ color: HEADER_BG, mr: 1, flexShrink: 0 }} />
          <Typography variant="body2">
            <Box component="span" sx={{ fontWeight: 600, color: theme.palette.text.cuarto }}>
              {t("ProveedoresPage.fieldEmail")}
            </Box>{" "}
            {proveedor.EMAIL || t("ProveedoresPage.notAvailable")}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Phone sx={{ color: "#34afc7", mr: 1, flexShrink: 0 }} />
          <Typography variant="body2">
            <Box component="span" sx={{ fontWeight: 600, color: theme.palette.text.cuarto }}>
              {t("ProveedoresPage.fieldPhone")}
            </Box>{" "}
            {proveedor.TELEFONO || t("ProveedoresPage.notAvailable")}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default ProveedoresFicha;
